import { useEffect, useRef, useState, useCallback } from "react";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";

export interface CoauthorPresence {
  userId: string;
  name: string;
  initials: string;
  colour: string;
  joinedAt: number;
  // F3 (2026-07-05): true while this person is actively typing in the editor
  // (cleared after a short idle). Lets other writers see "Sarah is editing
  // now" BEFORE they start changing the same piece — collision awareness
  // without a full CRDT.
  editing?: boolean;
}

export interface SaveBroadcast {
  userId: string;
  name: string;
  at: number;
}

// Cyrano-compatible co-author accent colours, deterministic per userId hash.
const COAUTHOR_COLOURS = [
  "#1C2951", // navy
  "#d4adbb", // pink-lavender
  "#2a3d6b", // navy mid
  "#6B6B6B", // warm grey
  "#2d8a56", // ok-green (used sparingly in the palette)
  "#b5392a", // err-red (accent, not alarm here)
];

function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

export function colourForUser(userId: string): string {
  return COAUTHOR_COLOURS[hashString(userId) % COAUTHOR_COLOURS.length];
}

export function initialsFrom(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

interface UseCoauthorPresenceArgs {
  channelKey: string | null;
  me: { userId: string; name: string } | null;
  onPeerSave?: (evt: SaveBroadcast) => void;
}

interface UseCoauthorPresenceReturn {
  peers: CoauthorPresence[];
  broadcastSave: () => void;
  /** F3: mark whether *I* am actively editing (re-tracks presence on change). */
  setEditing: (editing: boolean) => void;
  connected: boolean;
}

// Presence + save-broadcast over a single Realtime channel, keyed by
// `cyrano:editor:<channelKey>`. Peers excludes the current user.
export function useCoauthorPresence({
  channelKey,
  me,
  onPeerSave,
}: UseCoauthorPresenceArgs): UseCoauthorPresenceReturn {
  const [peers, setPeers] = useState<CoauthorPresence[]>([]);
  const [connected, setConnected] = useState(false);
  const channelRef = useRef<RealtimeChannel | null>(null);
  const onPeerSaveRef = useRef(onPeerSave);
  // F3: my own editing flag + stable joinedAt, so setEditing can re-track the
  // full presence payload without resetting my position in the strip.
  const editingRef = useRef(false);
  const joinedAtRef = useRef(0);

  useEffect(() => { onPeerSaveRef.current = onPeerSave; }, [onPeerSave]);

  useEffect(() => {
    if (!channelKey || !me) return;

    const topic = `cyrano:editor:${channelKey}`;
    const channel = supabase.channel(topic, {
      config: { presence: { key: me.userId } },
    });
    channelRef.current = channel;

    channel.on("presence", { event: "sync" }, () => {
      const state = channel.presenceState() as Record<string, Array<CoauthorPresence>>;
      const list: CoauthorPresence[] = [];
      for (const [uid, metas] of Object.entries(state)) {
        if (uid === me.userId) continue;
        const m = metas[0];
        if (!m) continue;
        list.push({
          userId: uid,
          name: m.name || "Collaborator",
          initials: m.initials || initialsFrom(m.name || "?"),
          colour: m.colour || colourForUser(uid),
          joinedAt: m.joinedAt || Date.now(),
          editing: !!(m as any).editing,
        });
      }
      list.sort((a, b) => a.joinedAt - b.joinedAt);
      setPeers(list);
    });

    channel.on("broadcast", { event: "saved" }, (payload) => {
      const evt = (payload.payload || {}) as Partial<SaveBroadcast>;
      if (!evt.userId || evt.userId === me.userId) return;
      onPeerSaveRef.current?.({
        userId: evt.userId,
        name: evt.name || "A co-author",
        at: evt.at || Date.now(),
      });
    });

    channel.subscribe(async (status) => {
      if (status === "SUBSCRIBED") {
        setConnected(true);
        joinedAtRef.current = Date.now();
        await channel.track({
          userId: me.userId,
          name: me.name,
          initials: initialsFrom(me.name),
          colour: colourForUser(me.userId),
          joinedAt: joinedAtRef.current,
          editing: editingRef.current,
        } satisfies CoauthorPresence);
      } else {
        setConnected(false);
      }
    });

    return () => {
      setConnected(false);
      setPeers([]);
      try { channel.untrack(); } catch { /* noop */ }
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
  }, [channelKey, me?.userId, me?.name]);

  const broadcastSave = useCallback(() => {
    const channel = channelRef.current;
    if (!channel || !me) return;
    channel.send({
      type: "broadcast",
      event: "saved",
      payload: { userId: me.userId, name: me.name, at: Date.now() } satisfies SaveBroadcast,
    });
  }, [me?.userId, me?.name]);

  // F3: flip my editing flag. Re-tracks only on an actual change so the
  // per-keystroke calls from the editor cost nothing after the first.
  const setEditing = useCallback((editing: boolean) => {
    if (editingRef.current === editing) return;
    editingRef.current = editing;
    const channel = channelRef.current;
    if (!channel || !me) return;
    void channel.track({
      userId: me.userId,
      name: me.name,
      initials: initialsFrom(me.name),
      colour: colourForUser(me.userId),
      joinedAt: joinedAtRef.current || Date.now(),
      editing,
    } satisfies CoauthorPresence);
  }, [me?.userId, me?.name]);

  return { peers, broadcastSave, setEditing, connected };
}
